/**
 * Profile Step Component
 *
 * Step 1: Collect user's name and organization info.
 *
 * @module components/onboarding/ProfileStep
 */

import { useState } from 'react';
import { Button, Input, Alert } from '@/components/ui';
import { ArrowRight } from 'lucide-react';
import type { ProfileData } from '@/hooks/useOnboarding';

// =============================================================================
// Types
// =============================================================================

export interface ProfileStepProps {
  /** Callback when profile is submitted */
  onSubmit: (data: ProfileData) => Promise<void>;
  /** Whether the form is loading */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Success message to display (e.g., after email verification) */
  successMessage?: string | null;
  /** Initial values (for returning users) */
  initialValues?: {
    firstName?: string;
    lastName?: string;
    organizationName?: string;
  };
}

// =============================================================================
// Component
// =============================================================================

export function ProfileStep({
  onSubmit,
  loading,
  error,
  successMessage,
  initialValues,
}: ProfileStepProps) {
  const [firstName, setFirstName] = useState(initialValues?.firstName || '');
  const [lastName, setLastName] = useState(initialValues?.lastName || '');
  const [organizationName, setOrganizationName] = useState(
    initialValues?.organizationName || ''
  );

  const isValid = firstName.trim().length > 0 && lastName.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || loading) return;

    await onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      organizationName: organizationName.trim() || undefined,
    });
  };

  return (
    <div className="space-y-6">
      {successMessage && (
        <Alert variant="success">
          {successMessage}
        </Alert>
      )}

      <div>
        <h2 className="text-2xl font-display font-semibold text-gray-900">
          👋 Let's set up your profile
        </h2>
        <p className="text-gray-500 mt-2">
          Tell us a bit about yourself to personalize your experience.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="John"
            autoFocus
            required
          />
          <Input
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
            required
          />
        </div>

        <Input
          label="Organization name"
          value={organizationName}
          onChange={(e) => setOrganizationName(e.target.value)}
          placeholder={
            firstName ? `${firstName}'s Organization` : 'Your Organization'
          }
          hint="Optional. We'll set a default name if you skip this."
        />

        {error && (
          <p className="text-sm text-error-600 bg-error-50 px-3 py-2 rounded-lg">
            {error}
          </p>
        )}

        <Button
          type="submit"
          loading={loading}
          disabled={!isValid}
          rightIcon={<ArrowRight className="h-4 w-4" />}
        >
          Continue
        </Button>
      </form>
    </div>
  );
}
